import { BrowserPushService } from 'src/app/services/browser-push/browser-push.service';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';
import { AnimationOptions } from 'ngx-lottie';
import { INotification } from 'src/app/models';
import { NotificationTypes, UserSortType } from 'src/app/models/enums';
import {
  AuthService,
  HomeRouteService,
  LanguageService,
  MessageService,
  StorageService,
  UsersService,
  UtilsService,
} from 'src/app/services';
import { SuggestionService } from 'src/app/services/suggestion/suggestion.service';
import { LiteralService } from 'src/app/services/literal/literal.service';
import { initialNotificationState } from 'src/app/store/initialisation/notification';
import { images } from 'src/images';
import { APP } from 'src/app/config/app';

@Component({
  selector: 'app-sign-in',
  templateUrl: './sign-in.component.html',
  styleUrls: ['./sign-in.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class SignInComponent {
  public readonly MAX_ATTEMPTS = APP.SIGN_IN_MAX_ATTEMPTS;
  private readonly INITIAL_TIMER = APP.SIGN_IN_INITIAL_TIMER;
  private readonly PASSWORD_RESET_TIMER = APP.SIGN_IN_PASSWORD_RESET_TIMER;
  public readonly CODE_LENGTH = APP.SIGN_IN_CODE_LENGTH;
  private readonly PASSWORD_MIN_LENGTH = APP.PASSWORD_MIN_LENGTH;
  public readonly TIME_FINISHED = APP.SIGN_IN_TIME_FINISHED;

  public notification: INotification = initialNotificationState;
  public images = images;

  public username = '';
  public password = '';
  public code = '';
  public ephemeralToken = '';
  public rememberMe = false;

  public doingLogin = false;
  public verifyingCode = false;
  public errorVerifyingCode = false;
  public attempts = 0;

  public expiredPassword = false;
  public newPassword = '';
  public confirmPassword = '';
  public changingPassword = false;
  public disableChangingPassword = true;

  public timer: string = this.INITIAL_TIMER;
  private interval: any;

  public errorExpiredPassword: boolean = false;

  public loadingLogin: AnimationOptions = {
    path: '/assets/animations/loading-login-green.json',
  };

  constructor(
    public literalService: LiteralService,
    public authService: AuthService,
    private storageService: StorageService,
    public router: Router,
    public utilsService: UtilsService,
    public usersService: UsersService,
    public languageService: LanguageService,
    private messageService: MessageService,
    private browserPushService: BrowserPushService,
    private suggestionService: SuggestionService,
    private homeRouteService: HomeRouteService,
  ) {}

  timerCountdown(time: number) {
    this.interval = setInterval(() => {
      const minutes = Math.floor(time / 60);
      const seconds = time % 60;
      this.timer = `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
      time--;
      if (time < 0) {
        this.code = '';
        this.newPassword = '';
        this.confirmPassword = '';
        clearInterval(this.interval);
      }
    }, 1000);
  }

  public setPassword(event: any) {
    this.password = event.value;
  }

  public setNewPassword(event: any) {
    this.newPassword = event.value;
  }

  public setConfirmPassword(event: any) {
    this.confirmPassword = event.value;
  }

  public async onSubmit(resend?: boolean): Promise<void> {
    try {
      this.resetCodeState();
      this.errorExpiredPassword = false;
      this.doingLogin = true;

      const response = (await this.authService.login(
        this.username,
        this.password,
      )) as any;
      this.ephemeralToken = response.headers.get('X-BOD-EPHEMERAL-TOKEN');

      if (resend) {
        this.handleResendSuccess();
      }

      this.doingLogin = false;
      this.restartTimer(this.timer);
    } catch (error: HttpErrorResponse | any) {
      this.doingLogin = false;
      this.handleLoginError(error);
    }
  }

  private handleResendSuccess(): void {
    this.utilsService.showNotification(
      images.notifications.check,
      'login.resend',
      NotificationTypes.SUCCESS,
    );
    this.resetVerificationState();
  }

  private handleLoginError(error: HttpErrorResponse): void {
    if (error.status === 409) {
      this.handleExpiredPassword(error);
    } else {
      this.utilsService.showNotification(
        images.notifications.warning,
        'login.error',
        NotificationTypes.DANGER,
      );
    }
  }

  private handleExpiredPassword(error: HttpErrorResponse): void {
    this.expiredPassword = true;
    this.timer = this.PASSWORD_RESET_TIMER;
    this.ephemeralToken = error.headers.get('X-BOD-EPHEMERAL-TOKEN') || '';
    this.restartTimer(this.timer);
  }

  public timeToSeconds(timer: string): number {
    const [minutes, seconds] = timer.split(':').map(Number);
    return minutes * 60 + seconds;
  }

  async verifyCode(): Promise<void> {
    try {
      this.verifyingCode = true;
      const response = (await this.authService.verifyCode(
        this.username,
        this.code,
        this.ephemeralToken,
        this.rememberMe,
      )) as any;
      const refreshToken = response.headers.get('X-BOD-REFRESH-TOKEN');
      if (refreshToken) {
        this.authService.setCookie(
          APP.LOCAL_STORAGE_REFRESH_TOKEN,
          refreshToken,
          30,
        );
      }
      const userInfo = await this.usersService.getLoggedUser();
      this.authService.setCookie(
        APP.LOCAL_STORAGE_USER_ID,
        userInfo.id?.toString() || '',
        30,
      );
      await this.getData();
      this.verifyingCode = false;
      this.errorVerifyingCode = false;
    } catch (error: any) {
      this.handleVerifyCodeError(error);
    }
  }

  private handleVerifyCodeError(error: any): void {
    this.errorVerifyingCode = true;
    this.verifyingCode = false;
    this.attempts = error?.error?.attempts ?? error?.attempts;

    const hasExhaustedAttempts =
      Number(this.attempts) === this.MAX_ATTEMPTS ||
      this.timer === this.TIME_FINISHED;
    if (hasExhaustedAttempts) {
      this.resetCodeState();
      this.errorVerifyingCode = false;
    }

    this.showVerificationErrorNotification();
  }

  private showVerificationErrorNotification(): void {
    const remainingAttempts = this.MAX_ATTEMPTS - Number(this.attempts) || 0;
    this.utilsService.showNotification(
      images.notifications.warning,
      '',
      NotificationTypes.DANGER,
      this.literalService.get('login.verifying.error.title', true, {
        attempts: remainingAttempts,
      }),
      this.literalService.get('login.verifying.error.text', true),
      undefined,
      false,
    );
  }

  public async resetPassword(): Promise<void> {
    try {
      this.changingPassword = true;
      this.checkPasswords();
      if (this.timer === this.TIME_FINISHED) {
        this.onSubmit(true);
      }
      await this.authService.passwordReset(
        this.newPassword,
        this.ephemeralToken,
      );
      this.resetPasswordState();
    } catch (error: HttpErrorResponse | any) {
      this.handleResetPasswordError(error);
    } finally {
      this.changingPassword = false;
    }
  }

  private handleResetPasswordError(error: HttpErrorResponse): void {
    const errorMessages: Record<number, string> = {
      400: 'login.resetPassword.error.mismatch',
      409: 'login.resetPassword.error.sameAsOld',
    };

    const messageKey =
      errorMessages[error.status] || 'login.resetPassword.error.notTheSame';
    this.utilsService.showNotification(
      images.notifications.warning,
      messageKey,
      NotificationTypes.DANGER,
    );
    this.errorExpiredPassword = true;
  }

  private checkPasswords(): void {
    this.errorExpiredPassword = this.newPassword !== this.confirmPassword;
    if (this.errorExpiredPassword) {
      throw new Error('Passwords do not match');
    }
  }

  public checkPasswordLength(): void {
    this.disableChangingPassword =
      this.newPassword.length < this.PASSWORD_MIN_LENGTH ||
      this.confirmPassword.length < this.PASSWORD_MIN_LENGTH;
  }

  private resetCodeState(): void {
    this.code = '';
  }

  private resetVerificationState(): void {
    this.attempts = 0;
    this.timer = this.INITIAL_TIMER;
    this.errorVerifyingCode = false;
  }

  private resetPasswordState(): void {
    this.expiredPassword = false;
    this.newPassword = '';
    this.confirmPassword = '';
    this.ephemeralToken = '';
    this.clearTimer();
  }

  public clearTimer(): void {
    clearInterval(this.interval);
    this.timer = this.INITIAL_TIMER;
  }

  private restartTimer(time: string): void {
    this.clearTimer();
    this.timerCountdown(this.timeToSeconds(time));
  }

  async getData(): Promise<void> {
    const token = await this.authService.getToken();

    if (!token) {
      this.utilsService.showNotification(
        images.notifications.warning,
        'login.error',
        NotificationTypes.DANGER,
      );
      return;
    }

    await this.initializeUserSession();
    this.initializeLanguage();
    void this.navigateToHomeRoute();
    this.messageService.startPolling();
    this.suggestionService.startPolling();
    void this.browserPushService.enable();
  }

  private async initializeUserSession(): Promise<void> {
    await this.storageService.setUserSortType(UserSortType.CREATION);

    await Promise.all([this.storeUserInfo(), this.storeClients()]);
  }

  private async storeUserInfo(): Promise<void> {
    const userInfo = await this.usersService.getLoggedUser();
    await this.storageService.setUserInfo(JSON.stringify(userInfo));
  }

  private async storeClients(): Promise<void> {
    const clients = await this.usersService.getClients();
    await this.storageService.setClients(JSON.stringify(clients));
  }

  private async initializeLanguage(): Promise<void> {
    const localLanguage = await this.storageService.getLanguage();
    if (localLanguage) return;

    const browserLocale = this.getBrowserLocale();
    this.languageService.setDefaultLanguage(browserLocale);
    this.languageService.setLanguage(browserLocale);
  }

  private getBrowserLocale(): string {
    const [locale] = navigator.language.split('-');
    return locale || navigator.language;
  }

  private async navigateToHomeRoute(): Promise<void> {
    await this.router.navigate([await this.homeRouteService.getHomeRoute()]);
  }

  ngOnDestroy(): void {
    clearInterval(this.interval);
  }
}
